import type {
  OwnedThreadCommentPayload,
  PendingThreadCommentEntry,
  ThreadCommentInput,
} from '@/modules/threads/comment-commands.api'
import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { queryKeys } from '@/hooks/query-keys'
import {
  flushOfflineLedger,
  offlineLedger,
  offlineRunnerContext,
  restoreOfflineLedger,
} from '@/lib/offline/ledger'
import {
  createThreadCommentCommandFn,
  ownedCommentPayload,
  selectPendingThreadComments,
} from '@/modules/threads/comment-commands.api'
import { useAuth } from '@/store/auth.store'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { ThreadComment } from '@scrambl/shared'
import type { OfflineMutationEnvelope } from '@scrambl/shared/offline'
import {
  canRetryOfflineWrite,
  createLedgerView,
  enqueueOfflineWrite,
  OfflineWriteIntentConflictError,
} from '@scrambl/shared/offline'
import { SessionChangedError } from '@scrambl/shared/session-scope'

import { useThreadComposerSession } from './useThreadComposerSession'

const view = createLedgerView(offlineLedger)
type Lease = ReturnType<typeof useThreadComposerSession>
type Captured = {
  envelope: OfflineMutationEnvelope<OwnedThreadCommentPayload>
  lease: Lease
}

/** One owned ledger for initial sends, explicit recovery and reconnect replay. */
export function useThreadCommentCommands(threadId?: string | null) {
  const lease = useThreadComposerSession()
  const user = useAuth(state => state.user)
  const client = useQueryClient()
  const snapshot = useSyncExternalStore(view.subscribe, view.read, view.read)
  const pending = useMemo(
    () =>
      lease.enabled
        ? selectPendingThreadComments(snapshot, lease.owner, threadId)
        : [],
    [snapshot, lease, threadId],
  )
  const invalidateReaders = (captured: Captured) => {
    if (!captured.lease.isCurrent()) return
    // Let current readers choose content and counts; never seed another reader's
    // cache from a writer result or increment a replayed receipt twice.
    void client.invalidateQueries({ queryKey: queryKeys.threads.all })
    const payload = captured.envelope.payload
    if (payload.moduleType === 'TASK')
      void client.invalidateQueries({ queryKey: ['tasks'] })
    if (payload.moduleType === 'TICKET')
      void client.invalidateQueries({ queryKey: queryKeys.tickets.all })
  }
  const mutation = useMutation<ThreadComment, Error, Captured>({
    // The owned ledger captures offline failures; a second paused mutation copy
    // would give two independent recovery paths for the same local command.
    networkMode: 'always',
    retry: false,
    mutationFn: ({ envelope, lease: captured }) =>
      captured.run(new AbortController().signal, signal =>
        createThreadCommentCommandFn(
          {
            ...offlineRunnerContext,
            captureSession: () => ({ assertCurrent: captured.assertCurrent }),
          },
          () => {
            captured.assertCurrent()
            return captured.owner
          },
          signal,
        )(envelope),
      ),
    onSuccess: (_result, captured) => invalidateReaders(captured),
  })
  const capture = async (input: ThreadCommentInput): Promise<Captured> => {
    lease.assertCurrent()
    if (!lease.owner) throw new SessionChangedError()
    const author =
      user?.id === lease.owner.userId
        ? {
            id: user.id,
            firstName: user.firstName ?? null,
            lastName: user.lastName ?? null,
            displayName: user.displayName ?? null,
            email: user.email ?? null,
            image: user.image ?? null,
          }
        : undefined
    const payload = ownedCommentPayload(input, lease.owner, author)
    const snapshotPayload = structuredClone(payload)
    await restoreOfflineLedger()
    lease.assertCurrent()
    const envelope = enqueueOfflineWrite<OwnedThreadCommentPayload>(
      offlineLedger,
      {
        kind: 'thread-comment.create',
        ownerId: lease.owner.userId,
        organizationId: lease.owner.organizationId,
        idempotencyKey: payload.clientMessageId!,
        label: 'Discussion message',
        payload: snapshotPayload,
      },
    )
    await flushOfflineLedger()
    lease.assertCurrent()
    return { envelope, lease }
  }
  const mutateAsync = mutation.mutateAsync
  const replayQueued = useCallback(async (): Promise<ThreadComment[]> => {
    const completed: ThreadComment[] = []
    if (!lease.isCurrent()) return completed
    const queued = selectPendingThreadComments(
      offlineLedger.list(),
      lease.owner,
      threadId,
    )
    for (const entry of queued) {
      if (!lease.isCurrent()) return completed
      if (entry.status !== 'queued' && entry.status !== 'inflight') continue
      if (Date.now() - entry.updatedAt < 2000 && entry.attempts > 0) continue
      try {
        const row = await mutateAsync({ envelope: entry.envelope, lease })
        if (lease.isCurrent()) completed.push(row)
      } catch {
        if (!lease.isCurrent()) return completed
      }
    }
    return completed
  }, [lease, threadId, mutateAsync])
  return {
    pending,
    lease,
    isPending: mutation.isPending,
    async capture(input: ThreadCommentInput) {
      const command = await capture(input)
      command.lease.assertCurrent()
      return command.envelope
    },
    async dispatch(
      envelope: OfflineMutationEnvelope<OwnedThreadCommentPayload>,
    ): Promise<ThreadComment> {
      lease.assertCurrent()
      if (
        !selectPendingThreadComments(
          [{ envelope, status: 'queued', attempts: 0, updatedAt: Date.now() }],
          lease.owner,
          threadId,
        ).length
      )
        throw new OfflineWriteIntentConflictError()
      const result = await mutation.mutateAsync({ envelope, lease })
      lease.assertCurrent()
      return result
    },
    async mutateAsync(input: ThreadCommentInput): Promise<ThreadComment> {
      const command = await capture(input)
      const result = await mutation.mutateAsync(command)
      command.lease.assertCurrent()
      return result
    },
    async retry(entry: PendingThreadCommentEntry): Promise<ThreadComment> {
      lease.assertCurrent()
      if (
        !selectPendingThreadComments([entry], lease.owner, threadId).length ||
        offlineLedger.get(
          entry.envelope.idempotencyKey,
          entry.envelope.ownerId,
        ) !== entry
      )
        throw new OfflineWriteIntentConflictError()
      if (entry.status !== 'queued' && entry.status !== 'inflight') {
        if (!canRetryOfflineWrite(entry))
          throw new OfflineWriteIntentConflictError()
        // Explicit user recovery keeps the exact original body and identity.
        offlineLedger.patch(
          entry.envelope.idempotencyKey,
          { status: 'queued', attempts: 0, message: undefined },
          entry.envelope.ownerId,
        )
      }
      const result = await mutation.mutateAsync({
        envelope: entry.envelope,
        lease,
      })
      lease.assertCurrent()
      return result
    },
    replayQueued,
  }
}
